// src/pages/finance-portal/reports/CohortAnalysis.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Alert, Grid,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    FormControl, InputLabel, Select, MenuItem, CircularProgress, Stack,
} from '@mui/material';
import GridOnIcon from '@mui/icons-material/GridOn';
import axiosInstance from '../../../utils/axiosInstance';
import {
    fontFamily, ACCENT, formatCurrency, todayIso,
} from './reportShared';

function heatColor(percent) {
    if (percent === null || percent === undefined) return 'transparent';
    const p = Math.max(0, Math.min(100, percent)) / 100;
    const g = Math.round(220 - (220 - 100) * p);
    return `rgba(34, 197, 94, ${0.15 + p * 0.6})`;
}

export default function CohortAnalysis() {
    const [type, setType] = useState('REVENUE');
    const [end, setEnd] = useState(todayIso());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            const res = await axiosInstance.get('/api/finance/analytics/cohorts', {
                params: { type, end_date: end },
            });
            if (res.data.success) setData(res.data);
            else setError(res.data.message || 'Failed');
        } catch (e) {
            setError(e.response?.data?.message || 'Failed to load cohorts');
        } finally { setLoading(false); }
    }, [type, end]);

    useEffect(() => { load(); }, [load]);

    const maxPeriods = data?.cohorts?.reduce((m, c) => Math.max(m, c.periods.length), 0) || 0;

    return (
        <Box sx={{ p: 3, fontFamily }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <GridOnIcon sx={{ color: ACCENT }} />
                <Typography variant="h5" sx={{ fontFamily, fontWeight: 700 }}>Cohort Analysis</Typography>
            </Stack>

            <Paper sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Cohort Type</InputLabel>
                            <Select label="Cohort Type" value={type}
                                onChange={(e) => setType(e.target.value)}>
                                <MenuItem value="REVENUE">Revenue</MenuItem>
                                <MenuItem value="EXPENSE">Expense</MenuItem>
                                <MenuItem value="EMPLOYEE">Employee</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField label="End" type="date" fullWidth size="small"
                            InputLabelProps={{ shrink: true }}
                            value={end} onChange={(e) => setEnd(e.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={4} sx={{ textAlign: 'right' }}>
                        <Button onClick={load} variant="contained"
                            sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#15803d' } }}>Run</Button>
                    </Grid>
                </Grid>
            </Paper>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {loading && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>}

            {data && !loading && (
                <Paper sx={{ p: 2, overflowX: 'auto' }}>
                    {(data.cohorts || []).length === 0 ? (
                        <Alert severity="info">No cohorts found for this period.</Alert>
                    ) : (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Cohort</TableCell>
                                        <TableCell align="right">Customers</TableCell>
                                        <TableCell align="right">Base</TableCell>
                                        {Array.from({ length: maxPeriods }, (_, i) => (
                                            <TableCell key={i} align="right">M{i}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(data.cohorts || []).map((c) => (
                                        <TableRow key={c.cohort_period}>
                                            <TableCell sx={{ fontWeight: 600 }}>{c.cohort_period}</TableCell>
                                            <TableCell align="right">{c.customer_count}</TableCell>
                                            <TableCell align="right">{formatCurrency(c.base_revenue)}</TableCell>
                                            {c.periods.map((p, i) => (
                                                <TableCell key={i} align="right"
                                                    sx={{ bgcolor: heatColor(p.retention_percent) }}>
                                                    {p.retention_percent.toFixed(0)}%
                                                </TableCell>
                                            ))}
                                            {Array.from({ length: maxPeriods - c.periods.length }, (_, i) => (
                                                <TableCell key={`x-${i}`} />
                                            ))}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Paper>
            )}
        </Box>
    );
}
